<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import ScheduledMessagesAPI from './api';
import {
  WASAPI_SCHEDULE_MESSAGE,
  WASAPI_SCHEDULED_MESSAGES_CHANGED,
} from './constants';
import {
  browserTimezone,
  defaultScheduleValue,
  isoToLocalInput,
  localInputToIso,
  windowHintKey,
} from './helpers';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';

const { t } = useI18n();
const inboxGetter = useMapGetter('inboxes/getInbox');

const dialogRef = ref(null);
const editingId = ref(null);
const conversationId = ref(null);
const inboxId = ref(null);
const content = ref('');
const templateParams = ref(null);
const scheduledAtLocal = ref(defaultScheduleValue());
const isSaving = ref(false);
const cancelOnReply = ref(false);
const showTemplates = ref(false);

const timezone = browserTimezone();
const inbox = computed(() => inboxGetter.value(inboxId.value) || {});
const isWhatsApp = computed(
  () => inbox.value?.channel_type === 'Channel::Whatsapp'
);
// WhatsApp: template picked = deliverable, hide the hint. Meta/TikTok: no escape, always warn.
const windowHint = computed(() => {
  const key = windowHintKey(inbox.value);
  return key === 'WINDOW_HINT' && templateParams.value ? null : key;
});
const canSave = computed(
  () =>
    !!conversationId.value &&
    !!scheduledAtLocal.value &&
    (content.value.trim() || templateParams.value) &&
    !isSaving.value
);

const open = ({
  conversationId: convId,
  inboxId: ibxId,
  content: text,
  scheduled,
} = {}) => {
  conversationId.value = convId;
  inboxId.value = ibxId;
  editingId.value = scheduled?.id || null;
  content.value = scheduled?.content ?? text ?? '';
  templateParams.value = scheduled?.template_params || null;
  cancelOnReply.value = !!scheduled?.cancel_on_reply;
  scheduledAtLocal.value = scheduled
    ? isoToLocalInput(scheduled.scheduled_at)
    : defaultScheduleValue();
  dialogRef.value?.open();
};

useEmitter(WASAPI_SCHEDULE_MESSAGE, open);

const onTemplatePicked = ({ message, templateParams: params }) => {
  content.value = message || '';
  templateParams.value = params || null;
  showTemplates.value = false;
};

const clearTemplate = () => {
  templateParams.value = null;
};

const save = async () => {
  if (!canSave.value) return;
  isSaving.value = true;
  const payload = {
    content: content.value.trim(),
    template_params: templateParams.value,
    scheduled_at: localInputToIso(scheduledAtLocal.value),
    timezone,
    cancel_on_reply: cancelOnReply.value,
  };
  try {
    if (editingId.value) {
      await ScheduledMessagesAPI.update(
        conversationId.value,
        editingId.value,
        payload
      );
    } else {
      await ScheduledMessagesAPI.create(conversationId.value, payload);
    }
    useAlert(t('WASAPI_SCHEDULED.SAVED'));
    emitter.emit(WASAPI_SCHEDULED_MESSAGES_CHANGED, conversationId.value);
    dialogRef.value?.close();
  } catch (error) {
    const data = error?.response?.data || {};
    useAlert(
      data.error ||
        (data.errors && Object.values(data.errors).flat().join(', ')) ||
        t('WASAPI_SCHEDULED.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="
      t(editingId ? 'WASAPI_SCHEDULED.EDIT_TITLE' : 'WASAPI_SCHEDULED.TITLE')
    "
    :description="t('WASAPI_SCHEDULED.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="templateParams"
        class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 outline-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('WASAPI_SCHEDULED.TEMPLATE_BADGE') }} ·
            {{ templateParams.name }}
            <template v-if="templateParams.language">
              ({{ templateParams.language }})
            </template>
          </span>
          <span class="flex-1" />
          <Button
            v-tooltip="t('WASAPI_SCHEDULED.CLEAR_TEMPLATE')"
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="clearTemplate"
          />
        </div>
        <p class="text-sm whitespace-pre-wrap text-n-slate-12">{{ content }}</p>
        <p class="text-xs text-n-slate-10">
          {{ t('WASAPI_SCHEDULED.TEMPLATE_MODE_HINT') }}
        </p>
      </div>
      <TextArea
        v-else
        v-model="content"
        :placeholder="t('WASAPI_SCHEDULED.CONTENT_PLACEHOLDER')"
        class="w-full"
        auto-height
      />
      <Button
        v-if="isWhatsApp && !templateParams"
        :label="t('WASAPI_SCHEDULED.USE_TEMPLATE')"
        icon="i-ph-whatsapp-logo"
        variant="faded"
        color="slate"
        size="sm"
        class="self-start"
        @click="showTemplates = true"
      />
      <Input
        v-model="scheduledAtLocal"
        type="datetime-local"
        :label="t('WASAPI_SCHEDULED.WHEN')"
      />
      <label
        class="flex items-center justify-between gap-3 p-3 rounded-xl outline outline-1 outline-n-weak"
      >
        <span class="flex flex-col">
          <span class="text-sm text-n-slate-12">{{
            t('WASAPI_SCHEDULER.CANCEL_ON_REPLY')
          }}</span>
          <span class="text-xs text-n-slate-10">{{
            t('WASAPI_SCHEDULER.CANCEL_ON_REPLY_HELP')
          }}</span>
        </span>
        <Switch v-model="cancelOnReply" />
      </label>
      <div
        v-if="windowHint"
        class="flex items-start gap-2 p-2.5 text-xs rounded-lg bg-n-amber-3/50 text-n-amber-11"
      >
        <Icon icon="i-lucide-triangle-alert" class="size-3.5 shrink-0 mt-0.5" />
        <span>{{ t(`WASAPI_SCHEDULED.${windowHint}`) }}</span>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WASAPI_SCHEDULED.SAVE')"
          color="blue"
          icon="i-lucide-clock"
          :disabled="!canSave"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </template>
    <WhatsappTemplates
      v-if="isWhatsApp"
      :inbox-id="inboxId"
      :show="showTemplates"
      @on-send="onTemplatePicked"
      @cancel="showTemplates = false"
      @update:show="showTemplates = $event"
    />
  </Dialog>
</template>
